import { useEffect } from 'react';

/**
 * Guarantees a visible, clickable close × icon on EVERY toast the app
 * renders — even the ones Sonner intentionally skips:
 *   • `toast.loading(...)`        (loading-type toasts)
 *   • `toast(<JSX/>, ...)`        (JSX-content / custom toasts)
 *   • `toast.promise(...)` while in the pending state
 *   • Toasts with an `action: { label: 'Undo', ... }` (Sonner shows the
 *     action button but its own X can be visually drowned out)
 *
 * Sonner's built-in `closeButton` prop is honoured for the standard
 * success/error/warning types, BUT the rule in sonner's source is:
 *   `closeButton && !toast.jsx && toastType !== 'loading'`
 * — so the two excluded categories never get a dismiss affordance.
 *
 * Approach: a single MutationObserver watches the toaster container for
 * any added `[data-sonner-toast]` element. For each toast we inject a
 * top-right ✕ button if one is missing, wire it to `toast.dismiss(id)`,
 * and stop. The injected button matches the existing styled close-X so
 * the UX is consistent across every notification type.
 *
 * Side-effect: zero call-site changes required. Existing `toast.success`
 * / `toast.error` / `toast.loading` / `toast.message` / `toast(...)` /
 * `toast.promise(...)` invocations across the codebase just work.
 */
export default function ToastCloseEnhancer() {
  useEffect(() => {
    // Lazy-import so the test environment (jsdom) doesn't try to evaluate
    // the dismiss helper before sonner is ready.
    let dismissFn = null;
    import('sonner').then((mod) => {
      dismissFn = mod.toast?.dismiss;
    });

    const SVG_X = `
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"
           aria-hidden="true" focusable="false">
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>`.trim();

    const ensureCloseButton = (toastEl) => {
      if (!toastEl || !(toastEl instanceof HTMLElement)) return;
      // Skip toasts that already carry our injected X (idempotent) and
      // toasts where Sonner already rendered one — we don't want two.
      if (toastEl.querySelector('[data-app-close-x]')) return;
      const existing = toastEl.querySelector('[data-close-button]');
      if (existing) {
        // The native Sonner close X exists — just normalize its position
        // and visibility so it reads consistently across types. The Toaster
        // classNames already do most of the work; this just enforces it for
        // edge-case types.
        existing.setAttribute('data-app-close-x', 'native');
        return;
      }

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('data-app-close-x', 'injected');
      btn.setAttribute('aria-label', 'Dismiss notification');
      btn.innerHTML = SVG_X;
      // Inline styles so a missing Tailwind purge or scope mismatch can't
      // hide the affordance. These mirror our themed close X in
      // App.js → toastOptions.classNames.closeButton.
      Object.assign(btn.style, {
        position: 'absolute',
        top: '8px',
        right: '8px',
        width: '22px',
        height: '22px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0',
        borderRadius: '9999px',
        border: '1px solid rgb(226, 232, 240)', // slate-200
        background: 'white',
        color: 'rgb(100, 116, 139)', // slate-500
        cursor: 'pointer',
        opacity: '1',
        transition: 'color .15s, background-color .15s, transform .15s',
        zIndex: '2',
      });
      btn.addEventListener('mouseenter', () => {
        btn.style.color = 'rgb(15, 23, 42)';        // slate-900
        btn.style.background = 'rgb(241, 245, 249)'; // slate-100
        btn.style.transform = 'scale(1.08)';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.color = 'rgb(100, 116, 139)';
        btn.style.background = 'white';
        btn.style.transform = 'scale(1)';
      });

      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        e.preventDefault();
        const id = toastEl.getAttribute('data-id') || toastEl.id;
        if (id && dismissFn) {
          dismissFn(id);
        } else {
          // Fallback: animate out by visually hiding, then remove on
          // transition end. Sonner's reconciler will GC the entry.
          toastEl.style.transition = 'opacity .15s, transform .15s';
          toastEl.style.opacity = '0';
          toastEl.style.transform = 'translateX(20px)';
          setTimeout(() => toastEl.remove(), 180);
        }
      });

      // Reserve right padding so the toast text never overlaps the X
      // — covers loading / JSX toasts that don't already use our
      // `pr-10` Tailwind override.
      if (!toastEl.style.paddingRight || parseInt(toastEl.style.paddingRight, 10) < 36) {
        toastEl.style.paddingRight = '40px';
      }
      // Position-context for the absolute close button.
      const computed = window.getComputedStyle(toastEl);
      if (computed.position === 'static') {
        toastEl.style.position = 'relative';
      }
      toastEl.appendChild(btn);
    };

    const scan = (root) => {
      if (!root) return;
      if (root.matches && root.matches('[data-sonner-toast]')) {
        ensureCloseButton(root);
      }
      root.querySelectorAll?.('[data-sonner-toast]').forEach(ensureCloseButton);
    };

    // Initial sweep (covers toasts that were rendered server-side or before
    // this component mounted in a hot-reload cycle).
    scan(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1) scan(n);
        });
        // Sonner re-uses the toast container and swaps content / classes
        // on type transitions (promise: loading → success). Re-scan the
        // parent so the new content gets a fresh injection check.
        if (m.type === 'attributes' && m.target instanceof HTMLElement) {
          scan(m.target);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-type', 'data-mounted'],
    });

    return () => observer.disconnect();
  }, []);

  return null;
}
